import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import { normalizeError } from './errors';
import { serializeParams, stripEmpty } from './params';
import type { QueryParams } from './params';

// One dedicated axios instance per backend convention (plan D5). No global singleton:
// QLT wants `Authorization: Bearer <jwt>`, IZTeach wants a raw `accessToken` header,
// so a shared instance would have to branch on URL for every request.

export interface AuthAdapter {
  // Headers merged into every request, re-read per request so token rotation is picked up.
  headers?: () => Record<string, string> | undefined;
  // Params merged into every request (IZTeach uses this for vendorId).
  params?: () => QueryParams | undefined;
  // Recover from a 401. Resolve true to replay the original request, false to give up.
  // Called at most once per burst of 401s - see the single-flight below.
  recover?: () => Promise<boolean>;
  // Called when recovery is impossible or fails. Wire sign-out here.
  onAuthFailure?: (message: string) => void;
}

export interface ClientOptions {
  timeout?: number;
  auth?: AuthAdapter;
}

const DEFAULT_TIMEOUT = 30000;

export function createClient(baseURL: string, options: ClientOptions = {}): AxiosInstance {
  const auth = options.auth || {};

  const instance = axios.create({
    baseURL,
    timeout: options.timeout || DEFAULT_TIMEOUT,
    paramsSerializer: { serialize: serializeParams },
  });

  instance.interceptors.request.use(
    (config) => {
      // config.headers is an AxiosHeaders instance; cast once and mutate it as a plain bag.
      const headers = ((config.headers as unknown as Record<string, unknown>) ||
        {}) as Record<string, unknown>;
      const extraHeaders = auth.headers ? auth.headers() : undefined;
      if (extraHeaders) {
        const keys = Object.keys(extraHeaders);
        for (let i = 0; i < keys.length; i += 1) {
          if (headers[keys[i]] === undefined) headers[keys[i]] = extraHeaders[keys[i]];
        }
      }
      (config as { headers?: unknown }).headers = headers;

      const extraParams = auth.params ? auth.params() : undefined;
      const merged: QueryParams = {};
      const current = (config.params as QueryParams) || {};
      const currentKeys = Object.keys(current);
      for (let i = 0; i < currentKeys.length; i += 1) {
        merged[currentKeys[i]] = current[currentKeys[i]];
      }
      if (extraParams) {
        const extraKeys = Object.keys(extraParams);
        for (let i = 0; i < extraKeys.length; i += 1) {
          if (merged[extraKeys[i]] === undefined) merged[extraKeys[i]] = extraParams[extraKeys[i]];
        }
      }
      config.params = stripEmpty(merged);
      return config;
    },
    (error) => Promise.reject(normalizeError(error))
  );

  // Single-flight recovery. The shared promise IS the queue: every 401 in the burst
  // awaits the same recovery, then replays. Fixes the reference implementation, which
  // re-registered its interceptors on every refresh (leak) and kept the flag global.
  let pendingRecovery: Promise<boolean> | null = null;

  function recoverOnce(): Promise<boolean> {
    if (pendingRecovery) return pendingRecovery;
    const run = (auth.recover as () => Promise<boolean>)();
    const clear = () => {
      pendingRecovery = null;
    };
    // Deliberately not Promise.prototype.finally - missing on Chrome 58 WebViews.
    pendingRecovery = run.then(
      (ok) => {
        clear();
        return ok;
      },
      () => {
        clear();
        return false;
      }
    );
    return pendingRecovery;
  }

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const apiError = normalizeError(error);
      if (apiError.kind !== 'unauthorized') return Promise.reject(apiError);

      const config = ((error || {}) as Record<string, any>).config as
        | (Record<string, any> & { __retried?: boolean })
        | undefined;

      if (!auth.recover || !config || config.__retried) {
        if (auth.onAuthFailure) auth.onAuthFailure(apiError.message);
        return Promise.reject(apiError);
      }

      return recoverOnce().then((ok) => {
        if (!ok) {
          if (auth.onAuthFailure) auth.onAuthFailure(apiError.message);
          return Promise.reject(apiError);
        }
        config.__retried = true;
        return instance.request(config as AxiosRequestConfig);
      });
    }
  );

  return instance;
}
